import config from '../config/environment';
import { applyCreditSignToAmounts } from '../utils/creditNotes';
import { isShekel } from '../utils/foreignCurrency';

function foreignPayload(expense: any) {
  if (isShekel(expense?.currency)) {
    return {
      currency: 'ILS',
      original_net_amount: null,
      original_vat_amount: null,
      original_total_amount: null,
      fx_rate: null,
      fx_rate_date: null,
      fx_rate_overridden: false,
    };
  }
  return {
    currency: expense.currency,
    original_net_amount: expense.originalNetAmount ?? null,
    original_vat_amount: expense.originalVatAmount ?? null,
    original_total_amount: expense.originalTotalAmount ?? null,
    fx_rate: expense.fxRate ?? null,
    fx_rate_date: expense.fxRateDate || null,
    fx_rate_overridden: Boolean(expense.fxRateOverridden),
  };
}

// API Configuration
const API_BASE_URL = config.api.baseUrl;

// Common headers
const getHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

// API response interface
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  code?: string;
  existingId?: number | string;
}

export const DUPLICATE_RECEIPT_TOAST = 'החשבונית כבר קיימת במערכת.';

export function isDuplicateReceiptError(error?: string): boolean {
  if (!error) return false;
  const text = error.toLowerCase();
  return (
    text.includes('already in the system') ||
    text.includes('duplicate_receipt') ||
    error.includes('כבר קיימת')
  );
}

// Image URL response interface
interface ImageUrlResponse {
  url: string;
}

// Generic API request function
const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  try {
    // Ensure proper URL construction without double slashes
    const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const endpointPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${baseUrl}${endpointPath}`;
    
    const response = await fetch(url, {
      headers: getHeaders(),
      cache: 'no-store',
      ...options,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}`);
    }

    return { success: true, data };
  } catch (error) {
    console.error('API Error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

function blobToJpegBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('לא הצלחנו לקרוא את הקובץ להעלאה.'));
    reader.readAsDataURL(blob);
  });
}

function serverFailureDetail(data: any): string {
  if (!data || typeof data !== 'object') {
    return '';
  }
  return [data.error, data.message]
    .filter((value) => typeof value === 'string' && value.trim())
    .join(' | ')
    .slice(0, 2000);
}

function parseProcessUploadResponse(
  response: Response,
  data: any
):
  | { success: true; data: { id: number | string; s3_image_key?: string } }
  | {
      success: false;
      error: string;
      code?: string;
      existingId?: number | string;
      status?: number;
      serverMessage?: string;
    } {
  if (response.status === 409) {
    return {
      success: false,
      error: data.error || 'החשבונית כבר קיימת במערכת.',
      code: 'duplicate_receipt',
      existingId: data.existing_id,
      status: response.status,
    };
  }
  if (response.status === 422) {
    return {
      success: false,
      error: data.error || 'לא הצלחנו לקרוא את החשבונית. נא לשלוח תמונה ברורה יותר.',
      code: 'unreadable',
      status: response.status,
    };
  }
  if (!response.ok) {
    const serverMessage = serverFailureDetail(data);
    return {
      success: false,
      error: data?.error || data?.message || `HTTP ${response.status}`,
      status: response.status,
      serverMessage: serverMessage || undefined,
    };
  }
  return { success: true, data: data as { id: number | string; s3_image_key?: string } };
}

function uploadNetworkError(error: unknown): string {
  const message = error instanceof Error ? error.message : '';
  if (/failed to fetch|networkerror|load failed/i.test(message)) {
    return 'ההעלאה נכשלה בחיבור לשרת. רעננו את הדף ונסו שוב.';
  }
  return message || 'Unknown error';
}

// API Methods
export const api = {
  auth: {
    login: (email: string, password: string) =>
      Promise.resolve({ success: false, error: 'Authentication not implemented yet' }),
    
    logout: () =>
      Promise.resolve({ success: false, error: 'Authentication not implemented yet' }),
    
    refreshToken: () =>
      Promise.resolve({ success: false, error: 'Authentication not implemented yet' }),
  },

  // Expenses
  expenses: {
    getAll: (params?: { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc'; filters?: any }) => {
      let url = '/expenses/get-data';
      if (params) {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.limit) queryParams.append('page_size', params.limit.toString());
        if (params.sortBy) queryParams.append('sort_by', params.sortBy);
        if (params.sortOrder) queryParams.append('sort_order', params.sortOrder);
        // tenant_id is handled automatically by JWT token
        url += `?${queryParams.toString()}`;
      }
      
      return apiRequest(url, { method: 'GET' });
    },
    
    fxRate: (currency: string, date: string) => {
      const params = new URLSearchParams({ currency, date });
      return apiRequest<{ currency: string; ils_per_unit: number; rate_date?: string }>(
        `/expenses/fx-rate?${params.toString()}`,
        { method: 'GET' }
      );
    },

    getById: (id: string) => {
      let url = `/expenses/get-data?id=${id}`;
      // tenant_id is handled automatically by JWT token
      return apiRequest(url, { method: 'GET' });
    },
    getVendors: (search?: string, limit?: number) => {
      let url = '/expenses/vendors';
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (limit) params.append('limit', limit.toString());
      if (params.toString()) url += `?${params.toString()}`;
      // tenant_id is handled automatically by JWT token
      return apiRequest(url, { method: 'GET' });
    },

    getProjectTags: (search?: string, limit?: number) => {
      let url = '/expenses/project-tags';
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (limit) params.append('limit', limit.toString());
      if (params.toString()) url += `?${params.toString()}`;
      return apiRequest(url, { method: 'GET' });
    },

    importRows: (rows: Array<Record<string, unknown>>) =>
      apiRequest<{
        created: Array<{ row: number; id: number }>;
        skipped: Array<{ row: number; reason: string; kind?: string; existingId?: number }>;
        createdCount: number;
        skippedCount: number;
      }>('/expenses/import', {
        method: 'POST',
        body: JSON.stringify({ rows }),
      }),

    getCpaSettings: () => apiRequest('/expenses/cpa-settings', { method: 'GET' }),

    putCpaSettings: (payload: Record<string, unknown>) =>
      apiRequest('/expenses/cpa-settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),

    cpaExport: (payload: Record<string, unknown> = {}) =>
      apiRequest('/expenses/cpa-export', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    
    create: async (expense: any) => {
      const noVat = Boolean(expense.noVat);
      const vatAmount = noVat ? null : expense.vatAmount || 0;
      const netAmount = expense.amount || 0;
      const totalAmount = noVat
        ? Number(expense.totalAmount) || netAmount
        : expense.totalAmount !== undefined && expense.totalAmount !== null && Number(expense.totalAmount) !== 0
          ? Number(expense.totalAmount)
          : netAmount + (vatAmount || 0);
      const isCredit = Boolean(expense.isCredit);
      const signed = isCredit
        ? applyCreditSignToAmounts(netAmount, vatAmount, totalAmount)
        : { netAmount, vatAmount: vatAmount ?? 0, totalAmount };

      // Map frontend expense fields to backend receipt fields
      const receiptData = {
        // tenant_id, submitted_by, and submitter_email will be set automatically by backend from JWT token
        vendor_name: expense.vendorName,
        category_1: expense.category1,
        category_2: expense.category2,
        category_3: expense.category3,
        net_amount: signed.netAmount,
        vat_amount: noVat ? null : signed.vatAmount,
        total_amount: signed.totalAmount,
        ...foreignPayload(expense),
        receipt_date: expense.documentDate,
        invoice_number: expense.documentNumber,
        document_type: isCredit ? 'credit_note' : 'invoice',
        notes: expense.notes, // Include notes field
        project_tag: expense.projectTag || '', // Include project tag field
        // Set default values for required fields
        business_id: 1, // Default business_id
        company_name: expense.vendorName,
        status: 'pending',
        confidence_level: 1.0, // Manual entry has high confidence
        recurrence_enabled: Boolean(expense.recurrenceEnabled),
        recurrence_frequency: expense.recurrenceEnabled ? expense.recurrenceFrequency : undefined,
        recurrence_count: expense.recurrenceEnabled ? Number(expense.recurrenceCount || 1) : undefined,
        recurrence_config: expense.recurrenceEnabled ? expense.recurrenceConfig : undefined,
      };

      return apiRequest('/expenses/post-data', {
        method: 'POST',
        body: JSON.stringify(receiptData)
      });
    },
    
    update: async (id: string, expense: any) => {
      // Only send fields that have actually changed
      const receiptData: any = {
        id: id,
        // tenant_id will be set automatically by backend from JWT token
      };

      // Helper function to get original backend value
      const getOriginalValue = (backendField: string) => {
        return expense.originalData?.[backendField];
      };

      // Normalize text so null/undefined/"" compare as the same empty value
      const normalizeText = (value: any) =>
        value === undefined || value === null ? '' : String(value).trim();

      const normalizeNumber = (value: any) => {
        if (value === undefined || value === null || value === '') return null;
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
      };

      const textChanged = (frontendField: string, backendField: string) => {
        if (expense[frontendField] === undefined) return false;
        return normalizeText(expense[frontendField]) !== normalizeText(getOriginalValue(backendField));
      };

      const numberChanged = (frontendField: string, backendField: string) => {
        if (expense[frontendField] === undefined) return false;
        return normalizeNumber(expense[frontendField]) !== normalizeNumber(getOriginalValue(backendField));
      };

      // Check each field and only include it if it has changed
      if (textChanged('vendorName', 'vendor_name')) {
        receiptData.vendor_name = expense.vendorName;
      }

      if (textChanged('category1', 'category_1')) {
        receiptData.category_1 = expense.category1;
      }

      if (textChanged('category2', 'category_2')) {
        receiptData.category_2 = expense.category2;
      }

      if (textChanged('category3', 'category_3')) {
        receiptData.category_3 = expense.category3;
      }

      if (expense.noVat) {
        const netAmount = expense.amount || 0;
        const totalAmount = Number(expense.totalAmount) || netAmount;
        receiptData.category_1 = expense.category1;
        receiptData.category_2 = expense.category2;
        receiptData.category_3 = expense.category3;
        receiptData.net_amount = totalAmount;
        receiptData.vat_amount = null;
        receiptData.total_amount = totalAmount;
      } else if (
        numberChanged('amount', 'net_amount') ||
        numberChanged('vatAmount', 'vat_amount') ||
        numberChanged('totalAmount', 'total_amount')
      ) {
        const vatAmount = expense.vatAmount || 0;
        const netAmount = expense.amount || 0;
        const totalAmount =
          expense.totalAmount !== undefined &&
          expense.totalAmount !== null &&
          Number(expense.totalAmount) !== 0
            ? Number(expense.totalAmount)
            : netAmount + vatAmount;
        receiptData.net_amount = netAmount;
        receiptData.vat_amount = vatAmount;
        receiptData.total_amount = totalAmount;
      }

      if (textChanged('documentDate', 'receipt_date')) {
        receiptData.receipt_date = expense.documentDate;
      }

      if (textChanged('projectTag', 'project_tag')) {
        receiptData.project_tag = normalizeText(expense.projectTag);
      }

      if (textChanged('notes', 'notes')) {
        receiptData.notes = expense.notes;
      }

      if (textChanged('documentNumber', 'invoice_number')) {
        receiptData.invoice_number = expense.documentNumber;
      }

      const fxBundle = foreignPayload(expense);
      const dateOnly = (value: any) => normalizeText(value).slice(0, 10);
      const fxChanged =
        normalizeText(fxBundle.currency) !== normalizeText(getOriginalValue('currency') || 'ILS') ||
        normalizeNumber(fxBundle.original_total_amount) !== normalizeNumber(getOriginalValue('original_total_amount')) ||
        normalizeNumber(fxBundle.fx_rate) !== normalizeNumber(getOriginalValue('fx_rate')) ||
        dateOnly(fxBundle.fx_rate_date) !== dateOnly(getOriginalValue('fx_rate_date')) ||
        Boolean(fxBundle.fx_rate_overridden) !== Boolean(getOriginalValue('fx_rate_overridden'));
      const amountsChanged = Boolean(
        receiptData.net_amount !== undefined ||
        receiptData.vat_amount !== undefined ||
        receiptData.total_amount !== undefined
      );
      if (fxChanged || amountsChanged) {
        Object.assign(receiptData, fxBundle);
      }

      // Always preserve image-related fields - never update them unless explicitly changed
      // These fields should remain unchanged to preserve image functionality
      if (expense.originalData) {
        // Only include these if they were explicitly changed (which shouldn't happen in normal editing)
        if (textChanged('s3_bucket', 's3_bucket')) {
          receiptData.s3_bucket = expense.originalData.s3_bucket;
        }
        if (textChanged('s3_key', 's3_key')) {
          receiptData.s3_key = expense.originalData.s3_key;
        }
        if (textChanged('s3_image_key', 's3_image_key')) {
          receiptData.s3_image_key = expense.originalData.s3_image_key;
        }
        if (textChanged('original_filename', 'original_filename')) {
          receiptData.original_filename = expense.originalData.original_filename;
        }
      }

      // If no fields have changed, return early
      if (Object.keys(receiptData).length === 1) { // Only 'id' field
        return { success: true, data: { id: id, action: 'no_changes' } };
      }

      return apiRequest('/expenses/post-data', {
        method: 'POST',
        body: JSON.stringify(receiptData)
      });
    },
    
    delete: async (id: string, options?: { mode?: 'single' | 'from_date' | 'series'; fromDate?: string }) => {
      const params = new URLSearchParams();
      if (options?.mode && options.mode !== 'single') {
        params.append('mode', options.mode);
      }
      if (options?.fromDate) {
        params.append('from_date', options.fromDate);
      }
      const query = params.toString();
      return apiRequest(`/expenses/${id}${query ? `?${query}` : ''}`, {
        method: 'DELETE'
      });
    },
  },

  // Categories - Not implemented in backend yet
  categories: {
    getAll: () =>
      Promise.resolve({ success: true, data: [] }),
    
    getTree: () =>
      Promise.resolve({ success: true, data: [] }),
    
    create: (category: any) =>
      Promise.resolve({ success: false, error: 'Categories not implemented yet' }),
    
    update: (id: string, category: any) =>
      Promise.resolve({ success: false, error: 'Categories not implemented yet' }),
    
    delete: (id: string) =>
      Promise.resolve({ success: false, error: 'Categories not implemented yet' }),
  },

  // Computer invoice upload (JPEG via presigned POST, then OCR)
  upload: {
    requestUrl: (filename: string) =>
      apiRequest<{
        url: string;
        fields: Record<string, string>;
        s3_key: string;
        max_bytes: number;
        original_filename?: string;
      }>('/expenses/upload-url', {
        method: 'POST',
        body: JSON.stringify({ filename, contentType: 'image/jpeg' }),
      }),

    putToS3: async (url: string, fields: Record<string, string>, file: Blob) => {
      const postOnce = async () => {
        const form = new FormData();
        Object.entries(fields).forEach(([key, value]) => form.append(key, value));
        form.append('file', file, 'invoice.jpg');
        return fetch(url, { method: 'POST', body: form });
      };
      let response = await postOnce();
      if (!response.ok) {
        response = await postOnce();
      }
      if (!response.ok) {
        return { success: false as const, error: `העלאה לשרת נכשלה (${response.status})` };
      }
      return { success: true as const };
    },

    process: async (originalFilename: string, image: Blob) => {
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      try {
        const imageBase64 = await blobToJpegBase64(image);
        const response = await fetch(`${baseUrl}/expenses/process-upload`, {
          method: 'POST',
          headers: getHeaders(),
          cache: 'no-store',
          body: JSON.stringify({
            original_filename: originalFilename,
            image_base64: imageBase64,
          }),
        });
        const data = await response.json().catch(() => ({}));
        return parseProcessUploadResponse(response, data);
      } catch (error) {
        return {
          success: false as const,
          error: uploadNetworkError(error),
        };
      }
    },

    processUrl: async (sourceUrl: string) => {
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      try {
        const response = await fetch(`${baseUrl}/expenses/process-upload`, {
          method: 'POST',
          headers: getHeaders(),
          cache: 'no-store',
          body: JSON.stringify({ source_url: sourceUrl }),
        });
        const data = await response.json().catch(() => ({}));
        return parseProcessUploadResponse(response, data);
      } catch (error) {
        return {
          success: false as const,
          error: uploadNetworkError(error),
        };
      }
    },
  },

  // Image URL
  image: {
    getSignedUrl: (file: string) => {
      const url = `/expenses/get-image?file=${encodeURIComponent(file)}`;
      return apiRequest<ImageUrlResponse>(url, { method: 'GET' });
    },
  },

  // Users - Not implemented in backend yet
  users: {
    getProfile: () =>
      Promise.resolve({ success: false, error: 'User profile not implemented yet' }),
    
    updateProfile: (profile: any) =>
      Promise.resolve({ success: false, error: 'User profile not implemented yet' }),
  },
};

export default api; 